// SPEC-45, SPEC-51 · the words of the Bilan presencial tab around its
// sections: the tab, the list of bilans, the bilan's header, the mosaic,
// the way between sections and the full screen. PT first (BRAND.md), FR and
// EN key for key. The protocol's own words (sections, fields, zones,
// lesions) are src/i18n/bilanProtocol*.ts. Labels are kept in sentence case
// and set in capitals by the screen; the arrows and the ✓ after a button's
// word are the screen's, hidden from a screen reader.

import type { Words } from "../shell";

export const bilanPt = {
  tab: "Bilan presencial",
  /** The name of the profile's segmented control, read by a screen reader. */
  tabs: "Separadores do perfil",
  appTab: "Avaliações na app",

  list: "Bilans",
  create: "Novo bilan",
  empty: "Ainda sem bilan presencial.",
  /** « 7 out 2026 ». */
  shortDate: (day: number, month: string, year: number) => `${day} ${month} ${year}`,
  /** « qua 7 out 2026 ». */
  longDate: (weekday: string, day: number, month: string, year: number) => `${weekday} ${day} ${month} ${year}`,
  /** « Dra. Ana Sousa · 6 / 14 secções ». */
  listMeta: (clinician: string, filled: number, total: number) => `${clinician} · ${filled} / ${total} ${total === 1 ? "secção" : "secções"}`,
  states: { progress: "Em curso", saved: "✓ Guardado" },
  privacyTitle: "Preenchido em sessão",
  privacyText: "Só a equipa clínica vê e edita. A atleta não recebe estes dados na app.",
  createFailed: "Não consegui criar o bilan. Tenta outra vez.",

  /** « Dra. Ana Sousa · 6 / 14 secções preenchidas ». */
  headerMeta: (clinician: string, filled: number, total: number) =>
    `${clinician} · ${filled} / ${total} ${total === 1 ? "secção preenchida" : "secções preenchidas"}`,
  save: "Guardar bilan",
  edit: "Editar",
  export: "Exportar",
  exporting: "A exportar…",
  saveFailed: "Não consegui guardar. Tenta outra vez.",
  exportFailed: "Não consegui exportar. Tenta outra vez.",
  notFound: "Este bilan já não existe.",

  /** A block of the mosaic and its sections with data: « Anamnese · 1 / 3 ». */
  block: (title: string, filled: number, total: number) => `${title} · ${filled} / ${total}`,
  fill: "Preencher",
  view: "Ver",
  /** 1.3 on its tile: « 2 zonas · 3 lesões ». */
  injuries: (zones: number, lesions: number) => `${zones} ${zones === 1 ? "zona" : "zonas"} · ${lesions === 1 ? "1 lesão" : `${lesions} lesões`}`,
  noHistory: "Sem antecedentes registados",
  lesions: (n: number) => (n === 1 ? "1 lesão" : `${n} lesões`),
  noLesions: "Sem lesões",
  /** The word after the big number of 1.3's header: « 2 zonas ». */
  zones: (n: number) => (n === 1 ? "zona" : "zonas"),
  /** A form section on its tile: « 4 / 12 campos ». */
  fields: (filled: number, total: number) => `${filled} / ${total} ${total === 1 ? "campo" : "campos"}`,
  toFill: "Por preencher",
  formStatus: { todo: "Por fazer", partial: "Incompleto", complete: "✓ Completo" },

  allBilans: "Todos os bilans",
  /** The full screen's line beside « ← Todos os bilans »: « Beatriz · qua 7 out 2026 ». */
  context: (athlete: string, date: string) => `${athlete} · ${date}`,
  /** « 2.2 · 5 / 14 ». */
  position: (number: string, rank: number, total: number) => `${number} · ${rank} / ${total}`,
  fullScreen: "Ecrã inteiro",
  exitFullScreen: "Sair do ecrã inteiro",
  noteLabel: "Observações do clínico",
  note: "Observações do clínico…",
  /** Near the note's limit: « 1950 / 2000 ». */
  noteCount: (length: number, max: number) => `${length} / ${max}`,
  previous: "Anterior",
  next: "Seguinte",
  skip: "Saltar",
  finish: "Terminar",
} as const;

// French typography: a narrow no-break space (U+202F) before ? ! ; : and
// inside « », a curly apostrophe. The words are the FR mockups'.
export const bilanFr: Words<typeof bilanPt> = {
  tab: "Bilan en présentiel",
  tabs: "Onglets du profil",
  appTab: "Évaluations dans l’app",

  list: "Bilans",
  create: "Nouveau bilan",
  empty: "Pas encore de bilan en présentiel.",
  shortDate: (day: number, month: string, year: number) => `${day} ${month} ${year}`,
  longDate: (weekday: string, day: number, month: string, year: number) => `${weekday} ${day} ${month} ${year}`,
  listMeta: (clinician: string, filled: number, total: number) => `${clinician} · ${filled} / ${total} ${total === 1 ? "section" : "sections"}`,
  states: { progress: "En cours", saved: "✓ Enregistré" },
  privacyTitle: "Rempli en séance",
  privacyText: "Seule l’équipe clinique voit et modifie. L’athlète ne reçoit pas ces données dans l’app.",
  createFailed: "Je n’ai pas pu créer le bilan. Réessaie.",

  headerMeta: (clinician: string, filled: number, total: number) =>
    `${clinician} · ${filled} / ${total} ${total === 1 ? "section remplie" : "sections remplies"}`,
  save: "Enregistrer le bilan",
  edit: "Modifier",
  export: "Exporter",
  exporting: "Export en cours…",
  saveFailed: "Je n’ai pas pu enregistrer. Réessaie.",
  exportFailed: "Je n’ai pas pu exporter. Réessaie.",
  notFound: "Ce bilan n’existe plus.",

  block: (title: string, filled: number, total: number) => `${title} · ${filled} / ${total}`,
  fill: "Remplir",
  view: "Voir",
  injuries: (zones: number, lesions: number) =>
    `${zones} ${zones === 1 ? "zone" : "zones"} · ${lesions === 1 ? "1 blessure" : `${lesions} blessures`}`,
  noHistory: "Aucun antécédent enregistré",
  lesions: (n: number) => (n === 1 ? "1 blessure" : `${n} blessures`),
  noLesions: "Aucune blessure",
  zones: (n: number) => (n === 1 ? "zone" : "zones"),
  fields: (filled: number, total: number) => `${filled} / ${total} ${total === 1 ? "champ" : "champs"}`,
  toFill: "À remplir",
  formStatus: { todo: "À faire", partial: "Incomplet", complete: "✓ Complet" },

  allBilans: "Tous les bilans",
  context: (athlete: string, date: string) => `${athlete} · ${date}`,
  position: (number: string, rank: number, total: number) => `${number} · ${rank} / ${total}`,
  fullScreen: "Plein écran",
  exitFullScreen: "Quitter le plein écran",
  noteLabel: "Observations du clinicien",
  note: "Observations du clinicien…",
  noteCount: (length: number, max: number) => `${length} / ${max}`,
  previous: "Précédent",
  next: "Suivant",
  skip: "Passer",
  finish: "Terminer",
};

export const bilanEn: Words<typeof bilanPt> = {
  tab: "In-person assessment",
  tabs: "Profile tabs",
  appTab: "In-app assessments",

  list: "Assessments",
  create: "New assessment",
  empty: "No in-person assessment yet.",
  shortDate: (day: number, month: string, year: number) => `${day} ${month} ${year}`,
  longDate: (weekday: string, day: number, month: string, year: number) => `${weekday} ${day} ${month} ${year}`,
  listMeta: (clinician: string, filled: number, total: number) => `${clinician} · ${filled} / ${total} ${total === 1 ? "section" : "sections"}`,
  states: { progress: "In progress", saved: "✓ Saved" },
  privacyTitle: "Filled in during the session",
  privacyText: "Only the clinical team sees and edits it. The athlete doesn’t get this data in the app.",
  createFailed: "I couldn’t create the assessment. Try again.",

  headerMeta: (clinician: string, filled: number, total: number) =>
    `${clinician} · ${filled} / ${total} ${total === 1 ? "section filled" : "sections filled"}`,
  save: "Save assessment",
  edit: "Edit",
  export: "Export",
  exporting: "Exporting…",
  saveFailed: "I couldn’t save. Try again.",
  exportFailed: "I couldn’t export. Try again.",
  notFound: "This assessment no longer exists.",

  block: (title: string, filled: number, total: number) => `${title} · ${filled} / ${total}`,
  fill: "Fill in",
  view: "View",
  injuries: (zones: number, lesions: number) =>
    `${zones} ${zones === 1 ? "zone" : "zones"} · ${lesions === 1 ? "1 injury" : `${lesions} injuries`}`,
  noHistory: "No injury history recorded",
  lesions: (n: number) => (n === 1 ? "1 injury" : `${n} injuries`),
  noLesions: "No injuries",
  zones: (n: number) => (n === 1 ? "zone" : "zones"),
  fields: (filled: number, total: number) => `${filled} / ${total} ${total === 1 ? "field" : "fields"}`,
  toFill: "To fill in",
  formStatus: { todo: "To do", partial: "Incomplete", complete: "✓ Complete" },

  allBilans: "All assessments",
  context: (athlete: string, date: string) => `${athlete} · ${date}`,
  position: (number: string, rank: number, total: number) => `${number} · ${rank} / ${total}`,
  fullScreen: "Full screen",
  exitFullScreen: "Exit full screen",
  noteLabel: "Clinician’s notes",
  note: "Clinician’s notes…",
  noteCount: (length: number, max: number) => `${length} / ${max}`,
  previous: "Previous",
  next: "Next",
  skip: "Skip",
  finish: "Finish",
};
